import { LocaleService } from '@next/core-lib/i18n';
import { AgrDataSource, SortableDataSource, Sorting } from '@next/core-lib/table-v2';
import { BehaviorSubject, combineLatest, map, Observable, Subject } from 'rxjs';
import { FarmResource, FarmResourcesDivision, Flock } from 'src/app/flock/models/flock.model';

export class FarmResourcesFlockDataSource
  extends AgrDataSource<FarmResourcesDivision>
  implements SortableDataSource<FarmResourcesDivision>
{
  private readonly destroy$ = new Subject<void>();
  readonly sortingSubject = new BehaviorSubject<Sorting>({});

  constructor(
    private readonly locale: LocaleService,
    public farmResources$: Observable<FarmResource[]>,
    public flocks$: Observable<Flock[]>,
    public flockResources$: Observable<FarmResourcesDivision[]>,
  ) {
    super();

    this.combineDataSources();
    this.loadingSubject.next(false);
  }

  applySorting(sorting: Sorting): void {
    this.sortingSubject.next(sorting);
  }

  connect(): Observable<FarmResourcesDivision[]> {
    return combineLatest([this.flockResources$, this.sortingSubject]).pipe(
      map(([resources, sorting]) => {
        const sortEntries = Object.entries(sorting);
        if (sortEntries.length > 0) {
          const [key, direction] = sortEntries[0];

          if (direction === '') {
            return resources;
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return resources.sort((a: any, b: any) => {
            const valueA = a[key];
            const valueB = b[key];
            const descending = direction === 'desc';

            if (valueA === null && valueB === null) return 0;
            if (valueA === null) return 1;
            if (valueB === null) return -1;

            if (typeof valueA === 'number' && typeof valueB === 'number') {
              return descending ? valueB - valueA : valueA - valueB;
            }

            if (valueA instanceof Date && valueB instanceof Date) {
              return descending
                ? valueB.getTime() - valueA.getTime()
                : valueA.getTime() - valueB.getTime();
            }

            return descending
              ? valueB.toString().localeCompare(valueA.toString())
              : valueA.toString().localeCompare(valueB.toString());
          });
        }

        return resources;
      }),
    );
  }

  disconnect(): void {
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  private combineDataSources(): Observable<FarmResourcesDivision[]> {
    this.flockResources$ = combineLatest([
      this.farmResources$,
      this.flockResources$,
      this.flocks$,
    ]).pipe(
      map(([farmResources, flockResources, flocks]) => {
        if (
          !farmResources ||
          farmResources.length === 0 ||
          !flockResources ||
          flockResources.length === 0 ||
          !flocks ||
          flocks.length === 0
        ) {
          return [];
        }

        return flockResources.map((flockRes) => {
          const flock = flocks.find((f) => f.id === flockRes.flockId);

          const resourceYears = Array.from(
            new Set(farmResources.flatMap((farmResource) => farmResource.year)),
          );

          return {
            ...flockRes,
            flockName: flock?.name,
            flockStartDate: flock?.arrival?.arrivalDate,
            flockEndDate: flock?.departure?.departureDate,
            status: this.getStatus(flock, resourceYears),
          };
        });
      }),
    );

    return this.flockResources$;
  }

  getStatus(flock: Flock, resourceYears: number[]) {
    const startYear = new Date(flock?.arrival?.arrivalDate).getFullYear();
    const flockEndDate = flock?.departure?.departureDate
      ? new Date(flock?.departure?.departureDate)
      : null;
    const flockEndYear = flockEndDate ? flockEndDate.getFullYear() : null;
    const currentDate = new Date();
    const allFlockYears: number[] = [];

    for (let year = startYear; year <= (flockEndYear ?? currentDate.getFullYear()); year++) {
      allFlockYears.push(year);
    }

    const hasAllResources = allFlockYears.every((year) => resourceYears.includes(year));

    if (hasAllResources && flockEndYear !== null && flockEndDate && flockEndDate <= currentDate) {
      return this.locale.translate('sustainability.resource.statuses.complete');
    }

    if (!flockEndDate || flockEndYear === null || flockEndDate >= currentDate) {
      return this.locale.translate('sustainability.resource.statuses.active');
    }

    return this.locale.translate('sustainability.resource.statuses.partial');
  }
}
